import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import type { CollapseProps, MenuProps } from 'antd'
import {
  Collapse,
  ConfigProvider,
  Layout,
  Menu,
  Spin,
  Tag,
  Tooltip,
} from 'antd'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'

type TicketMenuProps = {
  tickets: ReadonlyArray<LibraTicket>
  selectedTicketId?: string
  setSelectedTicketId: Dispatch<SetStateAction<string | undefined>>
  collapsed?: boolean
  extra?: ReadonlyArray<(ticket: LibraTicket) => ReactNode>
}

function ticketsToMenuItem(
  tickets: ReadonlyArray<LibraTicket>,
  collapsed?: boolean,
  extra?: ReadonlyArray<(ticket: LibraTicket) => ReactNode>
): MenuProps['items'] {
  return tickets.map((ticket) => ({
    key: ticket.id,
    label: collapsed ? (
      <Tooltip title={ticket.title} placement='right'>
        <FileTextOutlined />
      </Tooltip>
    ) : (
      <TicketLabelWrapper>
        <TicketLabelContainer>
          <FileTextOutlined /> {ticket.title}
        </TicketLabelContainer>
        {extra && extra.length > 0 && (
          <TicketExtraRow>
            {extra.map((renderExtra, index) => (
              <React.Fragment key={index}>{renderExtra(ticket)}</React.Fragment>
            ))}
          </TicketExtraRow>
        )}
      </TicketLabelWrapper>
    ),
  }))
}

const RawTicketMenu: FC<TicketMenuProps> = ({
  tickets,
  selectedTicketId,
  setSelectedTicketId,
  collapsed,
  extra,
}) => {
  const theme = useTheme()
  const [activeKey, setActiveKey] = useState<'open' | 'closed'>('open')

  const openTicketIds = useMemo(
    () =>
      new Set(
        tickets
          .filter((ticket) => !ticket.workflowComplete)
          .map((ticket) => ticket.id)
      ),
    [tickets]
  )

  const selectedTicket = useMemo(() => {
    return tickets.find((ticket) => ticket.id === selectedTicketId)
  }, [selectedTicketId, tickets])

  useEffect(() => {
    if (!selectedTicket) {
      return
    }
    setActiveKey(selectedTicket.workflowComplete ? 'closed' : 'open')
  }, [selectedTicket])

  const closedTicketIds = useMemo(
    () =>
      new Set(
        tickets
          .map((ticket) => ticket.id)
          .filter((id) => !openTicketIds.has(id))
      ),
    [openTicketIds, tickets]
  )

  const collapseItems = useMemo<CollapseProps['items']>(
    () => [
      {
        key: 'open',
        label: (
          <CollapseHeaderContainer>
            <span>{collapsed ? 'O' : 'Open Tickets'}</span>
            <Tag color='green'>{openTicketIds.size}</Tag>
          </CollapseHeaderContainer>
        ),
        children: (
          <TicketMenuAccordionContent
            collapsed={collapsed}
            tickets={tickets.filter((ticket) => openTicketIds.has(ticket.id))}
            selectedTicketId={
              selectedTicketId && openTicketIds.has(selectedTicketId)
                ? selectedTicketId
                : undefined
            }
            setSelectedTicketId={setSelectedTicketId}
            extra={extra}
          />
        ),
      },
      {
        key: 'closed',
        label: (
          <CollapseHeaderContainer>
            <span>{collapsed ? 'C' : 'Closed Tickets'}</span>
            <Tag color='gray'>{closedTicketIds.size}</Tag>
          </CollapseHeaderContainer>
        ),
        children: (
          <TicketMenuAccordionContent
            collapsed={collapsed}
            tickets={tickets.filter((ticket) => closedTicketIds.has(ticket.id))}
            selectedTicketId={
              selectedTicketId && closedTicketIds.has(selectedTicketId)
                ? selectedTicketId
                : undefined
            }
            setSelectedTicketId={setSelectedTicketId}
            extra={extra}
          />
        ),
      },
    ],
    [
      closedTicketIds,
      collapsed,
      extra,
      openTicketIds,
      selectedTicketId,
      setSelectedTicketId,
      tickets,
    ]
  )

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            itemSelectedBg: theme.forecast.background3,
            itemActiveBg: theme.forecast.background3,
            itemSelectedColor: theme.forecast.text1,
          },
        },
      }}>
      <StyledCollapse
        items={collapseItems}
        accordion
        activeKey={activeKey}
        onChange={([key]) =>
          setActiveKey((prev) =>
            key === undefined ? prev : key === 'closed' ? 'closed' : 'open'
          )
        }
      />
    </ConfigProvider>
  )
}

export const TicketMenuAccordionContent: FC<TicketMenuProps> = ({
  tickets,
  selectedTicketId,
  setSelectedTicketId,
  collapsed,
  extra,
}) => {
  const items = useMemo(
    () => ticketsToMenuItem(tickets, collapsed, extra),
    [tickets, collapsed, extra]
  )

  if (!items?.length) {
    return <TicketMenuPlaceholder>No tickets</TicketMenuPlaceholder>
  }

  return (
    <StyledTicketMenu
      mode='inline'
      selectedKeys={selectedTicketId ? [selectedTicketId] : []}
      items={items}
      onClick={({ key }) => setSelectedTicketId(String(key))}
    />
  )
}

export const CollapseHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

export const TicketMenuPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const StyledTicketMenu = styled(Menu)`
  border-inline-end: none !important;
  background: transparent !important;
  height: 100%;

  .ant-menu-item {
    height: 100%;
  }
`

const StyledCollapse = styled(Collapse)`
  height: 100%;
  .ant-collapse-content {
    height: 100%;
  }
`

const TicketLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const TicketLabelContainer = styled.span`
  display: flex;
  gap: 0.5rem;
  height: 1.8rem;
  align-items: center;
`

const TicketExtraRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding-bottom: 0.25rem;
`

export const TicketMenu = React.memo(RawTicketMenu)

type TicketMenuSiderProps = TicketMenuProps & {
  width: number
  collapsedWidth: number
  isLoading: boolean
  header?: ReactNode
}

const RawTicketMenuSider: FC<TicketMenuSiderProps> = ({
  width,
  collapsedWidth,
  isLoading,
  selectedTicketId,
  header,
  ...rest
}) => {
  const [collapsed, setCollapsed] = useState(false)

  useEffect(() => {
    setCollapsed(selectedTicketId ? true : false)
  }, [selectedTicketId])

  return (
    <StyledSider
      width={width}
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      collapsedWidth={collapsedWidth}>
      {!collapsed && header}
      {isLoading ? (
        <TicketMenuPlaceholder>
          <Spin size='small' />
        </TicketMenuPlaceholder>
      ) : (
        <TicketMenu
          selectedTicketId={selectedTicketId}
          collapsed={collapsed}
          {...rest}
        />
      )}
    </StyledSider>
  )
}

export const TicketMenuSider = React.memo(RawTicketMenuSider)

const StyledSider = styled(Layout.Sider)`
  background: ${({ theme }) => theme.forecast.background2} !important;
  .ant-layout-sider-trigger {
    background: ${({ theme }) => theme.forecast.background2} !important;
    color: ${({ theme }) => theme.forecast.text1};
    border-top: 1px solid ${({ theme }) => theme.forecast.background3};
  }
`
